import { useState, useEffect } from 'react';

/**
 * Custom hook for managing localStorage with React state
 * Provides a clean interface similar to useState but persisted
 * 
 * @param {string} key - localStorage key
 * @param {*} initialValue - initial value if key doesn't exist
 * @returns {[value, setValue]} - tuple similar to useState
 */
export const useLocalStorage = (key, initialValue) => {
    const [storedValue, setStoredValue] = useState(() => {
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.error(`Error reading localStorage key "${key}":`, error);
            return initialValue;
        }
    });

    const setValue = (value) => {
        try {
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            setStoredValue(valueToStore);
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
        } catch (error) {
            console.error(`Error setting localStorage key "${key}":`, error);
        }
    };

    return [storedValue, setValue];
};

/**
 * Custom hook for managing settings with fallback to IndexedDB
 * Useful for app settings that need persistence beyond localStorage
 * 
 * @param {string} settingKey - setting identifier
 * @param {*} defaultValue - default value
 * @returns {[value, setValue, loading]} - value, setter, and loading state
 */
export const useSetting = (settingKey, defaultValue) => {
    const [value, setValue] = useState(defaultValue);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadSetting = async () => {
            try {
                // Try localStorage first for performance
                const localValue = localStorage.getItem(`setting_${settingKey}`);
                if (localValue !== null) {
                    setValue(JSON.parse(localValue));
                    setLoading(false);
                    return;
                }

                // Fallback to IndexedDB if available
                if (window.db) {
                    const setting = await window.db.settings.where('key').equals(settingKey).first();
                    if (setting) {
                        setValue(setting.value);
                        // Cache in localStorage for faster access
                        localStorage.setItem(`setting_${settingKey}`, JSON.stringify(setting.value));
                    }
                }
            } catch (error) {
                console.error(`Error loading setting "${settingKey}":`, error);
            } finally {
                setLoading(false);
            }
        };

        loadSetting();
    }, [settingKey]);

    const updateSetting = async (newValue) => {
        try {
            setValue(newValue);
            localStorage.setItem(`setting_${settingKey}`, JSON.stringify(newValue));
            
            // Also update IndexedDB if available
            if (window.db) {
                await window.db.settings.put({ key: settingKey, value: newValue });
            }
        } catch (error) {
            console.error(`Error updating setting "${settingKey}":`, error);
        }
    };

    return [value, updateSetting, loading];
};